import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import SchoolPicker from '../components/SchoolPicker';
import AchievementBadgesSection from '../components/AchievementBadgesSection';
import apiService from '../services/api';
import { resolveChallengeIcon, ICON_KEY_FOR_TYPE } from '../constants/challengeTypes';
import '../styles/StaffPages.css';

const ICON_TINTS = {
  food: '#FF9B2B',
  bag: '#77D800',
  store: '#00C8FF',
  location: '#96694D',
  puzzle: '#00C8FF',
  football: '#FF9B2B',
  star: '#77D800',
  plane: '#00C8FF',
  card: '#96694D',
  calendar: '#FF9B2B',
  group: '#77D800',
  friend: '#00C8FF',
};

const FALLBACK_TINTS = ['#FF9B2B', '#77D800', '#96694D', '#00C8FF'];

const sameIds = (left, right) => {
  if (left.size !== right.size) return false;
  for (const id of left) {
    if (!right.has(id)) return false;
  }
  return true;
};

const iconTint = (item, index) =>
  ICON_TINTS[item?.icon_key] || ICON_TINTS[ICON_KEY_FOR_TYPE[item?.challenge_type]] || FALLBACK_TINTS[index % FALLBACK_TINTS.length];

const SuperuserChallengePresets = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'badges' ? 'badges' : 'challenges';
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [choseAll, setChoseAll] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [savedIds, setSavedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState('');
  const schoolId = selectedSchool?.id || '';
  const allSchools = choseAll && !selectedSchool;
  const dirty = !sameIds(selectedIds, savedIds);
  const activeSchoolCount = schools.filter(school => school.is_active !== false).length;

  const applyCatalog = data => {
    const results = data.results || [];
    const next = new Set(results.filter(row => row.selected).map(row => String(row.id)));
    setItems(results);
    setSelectedIds(next);
    setSavedIds(next);
  };

  useEffect(() => {
    const boot = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        const schoolsData = await apiService.getSchools();
        const schoolList = [...(Array.isArray(schoolsData) ? schoolsData : schoolsData.results || [])].sort((a, b) => {
          const aActive = a?.is_active === false ? 1 : 0;
          const bActive = b?.is_active === false ? 1 : 0;
          if (aActive !== bActive) return aActive - bActive;
          return (a?.name || '').localeCompare(b?.name || '');
        });
        setSchools(schoolList);
      } catch (error) {
        if (error.response?.status === 401) {
          navigate('/login/');
          return;
        }
        setPageError(error.response?.data?.error || 'Failed to load challenges.');
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [navigate]);

  useEffect(() => {
    if (loading) return undefined;
    if (!allSchools && !schoolId) {
      setItems([]);
      setSelectedIds(new Set());
      setSavedIds(new Set());
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setPageError('');
        const data = await apiService.getSchoolChallengeCatalog(allSchools ? null : schoolId);
        if (!cancelled) applyCatalog(data);
      } catch (error) {
        if (!cancelled) {
          setPageError(error.response?.data?.error || 'Failed to load challenges.');
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [allSchools, loading, schoolId]);

  const togglePreset = id => {
    const key = String(id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onCancel = () => {
    setSelectedIds(new Set(savedIds));
    setPageError('');
  };

  const setTab = next => {
    setSearchParams(next === 'badges' ? { tab: 'badges' } : {}, { replace: true });
  };

  const onSave = async () => {
    if (!allSchools && !schoolId) return;
    if (
      allSchools &&
      !window.confirm(`Apply these challenges to all ${activeSchoolCount} active schools?`)
    ) {
      return;
    }
    try {
      setSaving(true);
      setPageError('');
      const data = await apiService.saveSchoolChallengeCatalog(allSchools ? null : schoolId, Array.from(selectedIds));
      applyCatalog(data);
    } catch (error) {
      const data = error.response?.data;
      setPageError(data?.error || Object.values(data || {}).flat().join('\n') || 'Failed to save challenges.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to Superuser" to="/superuser/dashboard/" />
          <div className="staff-page-header-row hc-challenge-header-row">
            <div className="hc-challenge-switcher" role="tablist" aria-label="Challenges and badges">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'challenges'}
                className={`hc-challenge-switcher-btn${tab === 'challenges' ? ' is-active' : ''}`}
                onClick={() => setTab('challenges')}
              >
                Challenges
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'badges'}
                className={`hc-challenge-switcher-btn${tab === 'badges' ? ' is-active' : ''}`}
                onClick={() => setTab('badges')}
              >
                Badges
              </button>
            </div>
            {schools.length > 0 ? (
              <div className="hc-challenge-school-picker">
                <SchoolPicker
                  schools={schools}
                  value={selectedSchool}
                  onChange={school => {
                    setSelectedSchool(school);
                    setChoseAll(!school);
                  }}
                  allOptionLabel="All schools"
                  allSelected={choseAll}
                />
              </div>
            ) : null}
          </div>
          <p className="staff-page-subheader">
            {tab === 'badges'
              ? 'Select the badges available to users at this school.'
              : 'Choose which challenges this school can schedule.'}
          </p>

          {tab === 'challenges' && pageError ? (
            <div style={{ color: '#b00020', marginBottom: 16, whiteSpace: 'pre-wrap' }}>{pageError}</div>
          ) : null}
          {loading ? (
            <div>{tab === 'badges' ? 'Loading badges...' : 'Loading challenges...'}</div>
          ) : tab === 'badges' ? (
            !schoolId ? (
              <div className="hc-challenge-empty">Please select a school</div>
            ) : (
              <AchievementBadgesSection
                key={schoolId}
                schoolId={schoolId}
                schoolName={selectedSchool.name}
                hideHeader
              />
            )
          ) : !selectedSchool && !allSchools ? (
            <div className="hc-challenge-empty">Please select a school</div>
          ) : (
            <>
              <div className="hc-challenge-catalog-meta">
                {selectedIds.size} of {items.length} selected
              </div>
              {items.length === 0 ? (
                <div style={{ opacity: 0.7 }}>No solo challenge presets available.</div>
              ) : (
                <div className="hc-challenge-catalog-grid">
                  {items.map((item, index) => {
                    const selected = selectedIds.has(String(item.id));
                    const icon = resolveChallengeIcon(item);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`hc-challenge-catalog-card${selected ? ' hc-challenge-catalog-card--selected' : ''}`}
                        onClick={() => togglePreset(item.id)}
                      >
                        <span className="hc-challenge-catalog-check" aria-hidden="true">
                          {selected ? (
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M2.5 6.2L5 8.7L9.5 3.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          ) : null}
                        </span>
                        <span className="hc-challenge-catalog-icon" style={{ background: iconTint(item, index) }}>
                          {icon ? <img src={icon} alt="" width={18} height={18} /> : null}
                        </span>
                        <span className="hc-challenge-catalog-title">{item.title}</span>
                        <span className="hc-challenge-catalog-subtitle">{item.subtitle || item.challenge_type_label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="hc-challenge-catalog-footer">
                <div className="hc-challenge-catalog-footer-note">
                  {allSchools
                    ? `Selections apply to all ${activeSchoolCount} active schools.`
                    : `Selections apply only to ${selectedSchool.name}.`}
                </div>
                <div className="form-actions" style={{ marginTop: 0, paddingTop: 0 }}>
                  <button type="button" className="cancel-button" onClick={onCancel} disabled={!dirty || saving}>
                    Cancel
                  </button>
                  <button type="button" className="staff-page-button submit-button" onClick={onSave} disabled={!dirty || saving}>
                    <span className="staff-page-button-text">{saving ? 'Saving...' : 'Save changes'}</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperuserChallengePresets;
